import { createContext, useContext, useEffect, useRef, useState } from 'react'

import type { Response } from 'express-serve-static-core'

import type { AccessTokenContent } from '@b-stock/fe-core/bstock-react'
import {
  ManagedAccessToken,
  setCookieProvidedScopePlatformAgnostic,
} from '@b-stock/fe-core/bstock-react'

import Spinner from '@components/ui/spinner/Spinner'
import appConfig, { getHost } from '@config/config'
import CookieManager from '@helpers/CookieManager'
import { setClientSideManagedAccessToken } from '@helpers/xhr'

const AuthContext = createContext<{
  managedAccessToken: ManagedAccessToken | null
  accessToken: string | null
  decodedAccessToken: AccessTokenContent | null
}>({
  managedAccessToken: null,
  accessToken: null,
  decodedAccessToken: null,
})

export const useManagedAccessToken = () => {
  return useContext(AuthContext).managedAccessToken
}

export const useAccessToken = () => {
  return useContext(AuthContext).accessToken
}

export const useDecodedAccessToken = () => {
  return useContext(AuthContext).decodedAccessToken
}

const TokenLoading = Symbol('Token Loading')

export const AuthProvider = ({
  children,
  ssrResponse,
}: {
  children: React.ReactNode
  ssrResponse?: Response
}) => {
  const accountIdRef = useRef<string | null | typeof TokenLoading>(TokenLoading)

  const [latestAccessToken, setLatestAccessToken] = useState<{
    isLoading?: boolean
    encoded: string | null
    decoded: AccessTokenContent | null
  }>(() => ({
    isLoading: true,
    encoded: null,
    decoded: null,
  }))

  const managedAccessTokenRef = useRef<ManagedAccessToken | undefined>(
    undefined
  )

  useEffect(() => {
    const managedAccessToken = new ManagedAccessToken({
      getCookie: (key) => {
        return CookieManager.getCookie(key) ?? null
      },
      isGetCookieStale: !!ssrResponse,
      setCookie: ssrResponse
        ? ({ key, value, scope, options }) => {
            ssrResponse.cookie(key, value, {
              ...scope,
              ...options,
            })
            return Promise.resolve()
          }
        : setCookieProvidedScopePlatformAgnostic,
      onChange: (accessToken, decodedAccessToken) => {
        setLatestAccessToken({
          encoded: accessToken,
          decoded: decodedAccessToken,
        })
      },
      getHost,
      faClientId: appConfig.api.auth.clientId ?? 'MISSING_FA_CLIENT_ID',
    })
    managedAccessTokenRef.current = managedAccessToken
    if (!ssrResponse) {
      setClientSideManagedAccessToken(managedAccessToken)
    }
    return () => {
      setClientSideManagedAccessToken(null)
      managedAccessToken.dispose()
    }
  }, [ssrResponse])

  useEffect(() => {
    if (latestAccessToken.isLoading) {
      return
    }
    const currentAcctId = latestAccessToken.decoded?.accountId ?? null
    if (accountIdRef.current === TokenLoading) {
      accountIdRef.current = currentAcctId
    } else if (accountIdRef.current !== currentAcctId) {
      if (
        window.location.pathname.includes('/login') ||
        window.location.pathname.includes('/signup')
      ) {
        // Account ID changed - but we're in a process where that is expected to happen.
        // Skip the full page reload to avoid interrupting that process.
      } else {
        // Account ID changed - reload the page (accounts-portal can't currently handle that
        // without a reload, unfortunately)
        window.location.reload()
      }
    }
  }, [latestAccessToken])

  return (
    <AuthContext.Provider
      value={{
        managedAccessToken: managedAccessTokenRef.current ?? null, // oxlint-disable-line react/refs
        accessToken: latestAccessToken.encoded,
        decodedAccessToken: latestAccessToken.decoded,
      }}
    >
      {latestAccessToken.isLoading ? <Spinner /> : children}
    </AuthContext.Provider>
  )
}
